import getConfig from 'next/config';

import {patchApiRequest, postApiRequest, getApiRequest} from '~/src/utilities/feed/api';
import {getData, setData} from '~/src/utilities/helpers/context';
import {getRandomKey} from '~/src/utilities/helpers/string';
import {GtmEvents} from '~/src/utilities/constants/tracking';
import {trackGtm} from '~/src/utilities/tracking/gtm';
import {Logger} from '~/src/utilities/helpers/log';

const {publicRuntimeConfig: config} = getConfig();

const filterRating = (filter) => getData().ratings.filter((c) => c.id !== filter.id);

const isRated = (questionId, assetId, rating) => {
    const ratings = getData().ratings;
    return ratings.some((c) => c.questionId === questionId && c.assetId === assetId && c.rating === rating);
};

const getQuestions = async (assetId, contentType) => {
    try {
        const {collection} = await getApiRequest(`${config.feeds.ratings}?id=${encodeURIComponent(assetId)}&contentType=${encodeURIComponent(contentType)}`);

        return collection;
    } catch (error) {
        Logger.info(`[${error.name}][Rating] Error while getting rating questions`, {error});
    }

    return null;
};

const createRating = async (questionId, assetId, rating) => {
    const transientRating = {
        transient: true,
        id: getRandomKey(),
        questionId,
        assetId,
        rating
    };

    setData({ratings: [...getData().ratings, transientRating]});

    try {
        const {ratingId} = await postApiRequest(config.feeds.ratings, {questionId, assetId, rating});

        const ratingData = {
            id: ratingId,
            questionId,
            assetId,
            rating
        };

        setData({ratings: [...filterRating(transientRating), ratingData]});
    } catch (error) {
        Logger.info(`[${error.name}][Rating] Error while creating rating`, {error});

        setData({ratings: filterRating(transientRating)});
    }
};

const updateRating = async (existingRating, rating) => {
    const transientRating = {
        ...existingRating,
        transient: true,
        rating
    };

    setData({ratings: [...filterRating(existingRating), transientRating]});

    try {
        await patchApiRequest(`${config.feeds.ratings}/${existingRating.id}`, {rating});

        const ratingData = {
            ...existingRating,
            rating
        };

        setData({ratings: [...filterRating(transientRating), ratingData]});
    } catch (error) {
        Logger.info(`[${error.name}][Rating] Error while updating rating`, {error});

        setData({ratings: [...filterRating(transientRating), existingRating]});
    }
};

const rateAsset = (questionId, assetId, rating) => {
    const existingRating = getData().ratings.find((c) => c.questionId === questionId && c.assetId === assetId);

    if (!existingRating) {
        createRating(questionId, assetId, rating);

        return;
    }

    if (!existingRating.transient && existingRating.rating !== rating) {
        updateRating(existingRating, rating);
    }
};

const onRating = ({id, title, authors, fullUrlPath, type}) => (question, rating) => {
    trackGtm(GtmEvents.INTERACTION_RATING, {
        id,
        title,
        authors,
        fullUrlPath,
        type,
        question,
        rating
    });
};

export {isRated, getQuestions, rateAsset, onRating};
